/*
 * Copyright (c) 2025. Sergey S. Chernov - All Rights Reserved
 *
 *  You may use, distribute and modify this code under the
 * terms of the private license, which you must obtain from the author
 *
 * To obtain the license, contact the author: https://t.me/real_sergeych or email to
 * real dot sergeych at gmail.
 */

package net.sergeych.crypto2

import kotlin.time.Instant
import kotlinx.serialization.Serializable
import kotlinx.serialization.Transient
import net.sergeych.bipack.BipackDecoder
import net.sergeych.bipack.BipackEncoder
import net.sergeych.bipack.decodeFromBipack
import net.sergeych.utools.pack

/**
 * Multi-signed data box. Do not use the constructori directly, use [SealedBox.create]
 * instead to create the box and [SealedBox.plus] or [addSeal] to add more signatures
 * with different signing keys.
 * [SealedBox.contains] checks for a specific key signature presence.
 *
 * Signatures, [Seal], incorporate creation time and optional expiration which are
 * also signed and checked upon deserialization.
 *
 * It is serializable and checks integrity __on deserialization__. If any of seals does not
 * match the signed [message], it throws [IllegalSignatureException] _on deserialization_.
 * E.g., if you have it deserialized, it is ok, check it contains all needed keys among
 * signers.
 *
 * __The main constructor is used for deserializing only__. Don't use it directly unless you
 * know what you are doing as it may be dangerous.Use one of the above to create or change it.
 */
@Serializable
class SealedBox(
    val message: UByteArray,
    /**
     * [Seal] instance representing _correct signatures_ of this box. Note that if the box
     * is constructed (deserialized, etc) successfully, all seals are ok. Initial check
     * of signatures could be bypassed by setting [checkOnInit] to false, which should
     * be avoided.
     */
    val seals: List<Seal>,
    @Transient
    private val checkOnInit: Boolean = true
) {

    /**
     * Extract [VerifyingPublicKey] from [seals].
     */
    val signedByKeys: List<VerifyingPublicKey> by lazy { seals.map { it.publicKey } }

    @Suppress("unused")
    constructor(message: UByteArray, vararg keys: SigningKey) :
            this(message, keys.map { it.seal(message) } )

    /**
     * If this instance is not signed by a given key, return new instance signed also by this
     * key, or return unchanged (same) object if it is already signed by this key; you
     * _can't assume it always returns a copied object!_
     */
    operator fun plus(key: SigningKey): SealedBox =
        if (key.verifyingKey in this) this
        else SealedBox(message, seals + key.seal(message),false)

    /**
     * Add expiring seal, otherwise use [plus]. Overrides exising seal for [key]
     * if present:
     */
    fun addSeal(key: SigningKey, expiresAt: Instant?): SealedBox {
        val filtered = seals.filter { it.publicKey != key.verifyingKey }
        return SealedBox(message, filtered + key.seal(message, expiresAt), false)
    }

    /**
     * Check that it is signed with a specified key.
     */
    operator fun contains(publicKey: VerifyingKey): Boolean {
        return seals.any { it.publicKey == publicKey }
    }

    /**
     * Checks that the box is signed by enough keys to satisfy the given [Multikey].
     */
    @Suppress("unused")
    fun isSealedBy(multikey: Multikey) = multikey.check(signedByKeys)

    /**
     * Unpack bipack-encoded payload
     */
    @Suppress("unused")
    inline fun <reified T>unpack(): T = BipackDecoder.decode(message)

    init {
        if (seals.isEmpty()) throw IllegalArgumentException("there should be at least one seal")
        if (checkOnInit) {
            for( s in seals ) s.verify(message)
        }
    }

    val encoded: UByteArray by lazy { BipackEncoder.encode(this).toUByteArray() }

    companion object {
        /**
         * Create a new instance with a specific data sealed by one or more
         * keys. At least one key is required to disallow providing not-signed
         * instances, e.g. [SealedBox] is guaranteed to be properly sealed when
         * successfully instantiated.
         *
         * @param data a message to sign
         * @param keys a list of keys to sign with, should be at least one key.
         * @throws IllegalArgumentException if keys are not specified.
         */
        fun create(data: UByteArray, vararg keys: SigningKey): SealedBox {
            return SealedBox(data, keys.map { it.seal(data) }, false)
        }

        /**
         * Create a new instance serializing given data with Bipack and some
         * keys. At least one key is required to disallow providing not-signed
         * instances, e.g. [SealedBox] is guaranteed to be properly sealed when
         * successfully instantiated.
         *
         * @param payload an object to serialize and sign
         * @param keys a list of keys to sign with, should be at least one key.
         * @throws IllegalArgumentException if keys are not specified.
         */
        inline fun <reified T>new(payload: T,vararg keys: SigningKey): SealedBox =
            create(pack(payload), *keys)

        inline fun <reified T>encode(value: T, vararg keys: SigningKey): UByteArray =
            create(BipackEncoder.encode(value).toUByteArray(), *keys).encoded

        /**
         * Unpack and check sealed box, returning deserialized contained value
         * @throws IllegalSignatureException
         */
        inline fun <reified T>decode(packedSealedBox: UByteArray): T =
            BipackDecoder.decode<SealedBox>(packedSealedBox.asByteArray()).message.decodeFromBipack()

    }
}